import { useEffect, useState } from "react";
import { Calculator, MapPin, TrendingUp, BarChart3, Lightbulb,
              Download, FileSpreadsheet, Loader2, Plus, Trash2,
              Zap, Target } from "lucide-react";
import api, { formatErr } from "../lib/api";

/** Wave U — AI-Powered Cost Estimation panel.
 *  Five sub-tabs: Estimate · Rates · Materials · Benchmarks · What-If */
export default function CostEstimationPanel({ projectId }) {
  const [tab, setTab] = useState("estimate");
  if (!projectId) return null;
  const tabs = [
    { id: "estimate",   label: "Estimate",      Icon: Calculator },
    { id: "rates",      label: "Rates",         Icon: MapPin },
    { id: "materials",  label: "Material prices", Icon: TrendingUp },
    { id: "benchmarks", label: "Benchmarks",    Icon: BarChart3 },
    { id: "whatif",     label: "What-If",       Icon: Zap },
    { id: "opt",        label: "Optimisations", Icon: Lightbulb },
    { id: "inflation",  label: "Inflation & cash flow", Icon: TrendingUp },
  ];
  return (
    <div data-testid="cost-panel" style={{ padding: 16,
                              background: "var(--pf-bg)", color: "var(--pf-text)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <Calculator size={20} color="#F26B1F" />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Cost Estimation · Market Intelligence</div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            CPWD DSR 2024 base rates · location-indexed · AACE estimate classes ·
            live material prices · industry benchmarks · what-if scenarios
          </div>
        </div>
      </div>
      <div role="tablist" style={{ display: "flex", gap: 4,
              borderBottomStyle: "solid", borderBottomWidth: 1,
              borderBottomColor: "var(--pf-border)", marginBottom: 14 }}>
        {tabs.map(({ id, label, Icon }) => (
          <button key={id} data-testid={`cost-tab-${id}`}
              onClick={() => setTab(id)}
              style={{
                padding: "8px 14px", display: "flex", gap: 6,
                alignItems: "center", fontSize: 12,
                background: tab === id ? "rgba(242,107,31,0.15)" : "transparent",
                color: tab === id ? "#F26B1F" : "var(--pf-text)",
                borderTop: "none", borderLeft: "none", borderRight: "none",
                borderBottomStyle: "solid", borderBottomWidth: 2,
                borderBottomColor: tab === id ? "#F26B1F" : "transparent",
                cursor: "pointer", fontWeight: 600,
              }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      {tab === "estimate"   && <EstimateTab projectId={projectId} />}
      {tab === "rates"      && <RatesTab />}
      {tab === "materials"  && <MaterialsTab />}
      {tab === "benchmarks" && <BenchmarksTab />}
      {tab === "whatif"     && <WhatIfTab projectId={projectId} />}
      {tab === "opt"        && <OptimisationsTab projectId={projectId} />}
      {tab === "inflation"  && <InflationTab projectId={projectId} />}
    </div>
  );
}


const ITEM_CODES = [
  "RCC_M25", "RCC_M30", "REBAR_FE500", "REBAR_FE550",
  "BRICK_RED", "BRICK_AAC", "PCC_M10", "EXC_SOFT", "EXC_HARD",
  "PIPE_CS_6", "PIPE_CS_8", "PIPE_SS_4", "VALVE_GATE_6",
  "VALVE_CONTROL_4", "PSV_3X4",
  "CABLE_240", "TRAY_300", "TRAFO_1MVA", "DG_500", "LED_PANEL",
  "VRF_OUTDOOR", "AHU_5TR", "DUCT_GI",
  "PLASTER_INT", "PLASTER_EXT", "FLOOR_VITRIFIED", "PAINT_INT",
  "WATERPROOF", "SHUTTERING", "ALU_FORMWORK",
];
const STAGES = ["concept", "feasibility", "FEED", "DPR", "detail", "tender"];
const LOCATIONS = ["Mumbai","Delhi NCR","Bengaluru","Hyderabad","Chennai",
                              "Pune","Kolkata","Ahmedabad","Jaipur","Lucknow","Bhopal"];


function EstimateTab({ projectId }) {
  const [domain, setDomain]   = useState("real_estate");
  const [industry, setIndustry] = useState("residential_economy");
  const [stage, setStage]     = useState("FEED");
  const [location, setLocation] = useState("Delhi NCR");
  const [area, setArea]       = useState("1500");
  const [capacity, setCapacity] = useState("16146");
  const [currency, setCurrency] = useState("INR");
  const [applyPpp, setApplyPpp] = useState(false);
  const [rows, setRows] = useState([
    { item_code: "RCC_M25", quantity: 100 },
    { item_code: "REBAR_FE500", quantity: 10 },
    { item_code: "BRICK_RED", quantity: 200 },
  ]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const updateRow = (i, k, v) =>
      setRows(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows([...rows, { item_code: "RCC_M25", quantity: 1 }]);
  const delRow = (i) => setRows(rows.filter((_, j) => j !== i));

  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/cost/estimate", {
        project_id: projectId, project_name: "Project",
        domain, industry, design_stage: stage, location,
        total_area_m2: parseFloat(area) || null,
        capacity_or_area: parseFloat(capacity) || null,
        currency, apply_ppp: applyPpp,
        quantities: rows.map(r => ({
            item_code: r.item_code,
            quantity: parseFloat(r.quantity) || 0
        })),
        save: true,
      });
      setResult(r.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
    setBusy(false);
  };

  const download = async (fmt) => {
    try {
      const r = await api.get(`/cost/report/${projectId}${fmt === "xlsx" ? "?fmt=xlsx" : ""}`,
                                          { responseType: "blob" });
      const mime = fmt === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      window.open(url, "_blank");
    } catch (_) { /* ok */ }
  };

  return (
    <div data-testid="cost-estimate-tab">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                          gap: 8, marginBottom: 12 }}>
        <Field label="Domain">
          <select data-testid="estimate-domain" value={domain}
              onChange={e => setDomain(e.target.value)}
              className="pf-input" style={inp}>
            <option value="real_estate">Real estate</option>
            <option value="infrastructure">Infrastructure</option>
            <option value="process_plant">Process plant</option>
          </select>
        </Field>
        <Field label="Industry">
          <input data-testid="estimate-industry" value={industry}
              onChange={e => setIndustry(e.target.value)}
              placeholder="e.g. residential_economy" style={inp} />
        </Field>
        <Field label="Stage">
          <select data-testid="estimate-stage" value={stage}
              onChange={e => setStage(e.target.value)} style={inp}>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Location">
          <select data-testid="estimate-location" value={location}
              onChange={e => setLocation(e.target.value)} style={inp}>
            {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
        <Field label="Total area (m²)">
          <input data-testid="estimate-area" value={area}
              onChange={e => setArea(e.target.value)} style={inp} />
        </Field>
      </div>
      <Field label="Capacity / Area for benchmark (matches benchmark unit)">
        <input data-testid="estimate-capacity" value={capacity}
            onChange={e => setCapacity(e.target.value)} style={inp} />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                          gap: 8, marginTop: 8 }}>
        <Field label="Currency (multi-currency · spec §2.4)">
          <select data-testid="estimate-currency" value={currency}
              onChange={e => setCurrency(e.target.value)} style={inp}>
            <option value="INR">INR — Indian Rupee</option>
            <option value="USD">USD — US Dollar</option>
            <option value="AED">AED — UAE Dirham</option>
            <option value="SAR">SAR — Saudi Riyal</option>
            <option value="EUR">EUR — Euro</option>
            <option value="GBP">GBP — British Pound</option>
          </select>
        </Field>
        <label data-testid="estimate-ppp"
            style={{ fontSize: 11, marginTop: 18, color: "var(--pf-muted)",
                          display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={applyPpp}
              onChange={e => setApplyPpp(e.target.checked)} />
          Apply PPP adjustment (purchasing-power-parity for fair international comparison)
        </label>
      </div>

      <div className="pf-overline" style={{ marginTop: 12 }}>BOQ rows</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 32px",
                          gap: 6, marginTop: 6 }}>
        {rows.map((r, i) => (
          <Row key={i}>
            <select data-testid={`row-code-${i}`} value={r.item_code}
                onChange={e => updateRow(i, "item_code", e.target.value)}
                style={inp}>
              {ITEM_CODES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid={`row-qty-${i}`}
                value={r.quantity}
                onChange={e => updateRow(i, "quantity", e.target.value)}
                placeholder="qty" style={inp} />
            <button onClick={() => delRow(i)} className="btn btn-ghost"
                style={{ padding: 4 }}><Trash2 size={14} /></button>
          </Row>
        ))}
      </div>
      <button data-testid="add-row-btn" onClick={addRow}
          className="btn btn-ghost" style={{ marginTop: 6, fontSize: 12 }}>
        <Plus size={14} /> Add BOQ row
      </button>

      <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
        <button data-testid="estimate-run-btn" onClick={run} disabled={busy}
            className="btn btn-primary" style={{ fontSize: 12 }}>
          {busy ? <Loader2 size={14} className="pf-spin" /> : <Calculator size={14} />}
          Generate estimate
        </button>
        {result && (
          <>
            <button data-testid="report-pdf-btn" className="btn btn-ghost"
                style={{ fontSize: 12 }} onClick={() => download("pdf")}>
              <Download size={14} /> PDF
            </button>
            <button data-testid="report-xlsx-btn" className="btn btn-ghost"
                style={{ fontSize: 12 }} onClick={() => download("xlsx")}>
              <FileSpreadsheet size={14} /> XLSX
            </button>
          </>
        )}
      </div>
      {err && <div style={errBox}>⚠ {err}</div>}

      {result && (
        <div data-testid="estimate-result" style={{ marginTop: 14,
                              padding: 14, background: "var(--pf-card)",
                              border: "1px solid var(--pf-border)",
                              borderRadius: 6 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                              gap: 12, fontSize: 12 }}>
            <KV k="Total" v={`INR ${result.total_estimated_cost.toLocaleString("en-IN")}`} highlight />
            <KV k="Subtotal" v={`INR ${result.subtotal.toLocaleString("en-IN")}`} />
            <KV k="GST 18%" v={`INR ${result.gst.toLocaleString("en-IN")}`} />
            <KV k="Preliminaries 8%" v={`INR ${result.preliminaries.toLocaleString("en-IN")}`} />
            <KV k="Overhead 10%" v={`INR ${result.contractors_overhead.toLocaleString("en-IN")}`} />
            <KV k="Profit 8%" v={`INR ${result.contractors_profit.toLocaleString("en-IN")}`} />
            <KV k={`Contingency ${result.contingency_pct.toFixed(0)}%`}
                v={`INR ${result.contingency.toLocaleString("en-IN")}`} />
            <KV k="AACE class"
                v={`${result.estimate_accuracy.class} (${result.estimate_accuracy.accuracy})`} />
            <KV k="Location idx" v={result.location_index.toFixed(2)} />
            {result.cost_per_sqm && <KV k="Cost / m²" v={`INR ${result.cost_per_sqm.toLocaleString("en-IN")}`} />}
          </div>
          {result.benchmark && (
            <div data-testid="estimate-benchmark" style={{ marginTop: 12, padding: 10,
                                background: result.benchmark.comparison === "WITHIN RANGE"
                                                  ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                                borderRadius: 6, fontSize: 12 }}>
              <Target size={14} style={{ display: "inline", marginRight: 4 }} />
              <strong>{result.benchmark.comparison}</strong> · Your unit cost:
              {" "}<code>{result.benchmark.your_estimate_per_unit}</code> {result.benchmark.benchmark.unit}
              {" "}(benchmark range: {result.benchmark.benchmark.low}–{result.benchmark.benchmark.high},
              avg {result.benchmark.benchmark.avg}; Δ {result.benchmark.delta_pct}%)
            </div>
          )}
          {result.converted && (
            <div data-testid="estimate-converted" style={{ marginTop: 12, padding: 10,
                                background: "rgba(242,107,31,0.1)",
                                borderRadius: 6, fontSize: 12,
                                fontFamily: "'JetBrains Mono', monospace" }}>
              <strong>{result.converted.symbol} {result.converted.values.total_estimated_cost.toLocaleString("en-IN")}</strong>
              {" "}({result.converted.currency}) — converted at
              {" "}<code>{result.converted.rate_per_inr}</code> {result.converted.currency}/INR
              {" "}({result.converted.rate_kind === "ppp" ? "PPP-adjusted" : "spot FX"})
            </div>
          )}
          <div className="pf-overline" style={{ marginTop: 12, marginBottom: 6 }}>Line items</div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            <table style={{ width: "100%", fontSize: 11, fontFamily: "monospace" }}>
              <thead><tr style={{ color: "var(--pf-muted)" }}>
                <th align="left">Item</th><th align="right">Qty</th>
                <th align="left">Unit</th><th align="right">Rate</th>
                <th align="right">Amount</th>
              </tr></thead>
              <tbody>
                {result.line_items.map((it, i) => (
                  <tr key={i} style={{ borderTop: "1px solid var(--pf-border)" }}>
                    <td>{it.description}</td>
                    <td align="right">{it.quantity}</td>
                    <td>{it.unit}</td>
                    <td align="right">{it.rate.toLocaleString("en-IN")}</td>
                    <td align="right">{it.amount.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function RatesTab() {
  const [location, setLocation] = useState("Mumbai");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/cost/rates/${encodeURIComponent(location)}`)
        .then(r => setData(r.data)).catch(() => {});
  }, [location]);
  return (
    <div data-testid="cost-rates-tab">
      <Field label="Location">
        <select data-testid="rates-location" value={location}
            onChange={e => setLocation(e.target.value)} style={inp}>
          {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </Field>
      {data && (
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--pf-muted)" }}>
          Index: <strong style={{ color: "#F26B1F" }}>{data.location_index.toFixed(2)}</strong> ·
          {" "}{data.count} items
        </div>
      )}
      <div style={{ marginTop: 8, maxHeight: 480, overflowY: "auto",
                          border: "1px solid var(--pf-border)", borderRadius: 6 }}>
        <table style={{ width: "100%", fontSize: 11, fontFamily: "monospace" }}>
          <thead><tr style={{ background: "var(--pf-card)" }}>
            <th align="left">Code</th><th align="left">Description</th>
            <th align="left">Disc</th><th align="left">Unit</th>
            <th align="right">Base</th><th align="right">Adjusted</th>
          </tr></thead>
          <tbody>
            {(data?.items || []).map(i => (
              <tr key={i.item_code} style={{ borderTop: "1px solid var(--pf-border)" }}>
                <td>{i.item_code}</td><td>{i.description}</td>
                <td>{i.discipline}</td><td>{i.unit}</td>
                <td align="right">{i.base_rate.toLocaleString("en-IN")}</td>
                <td align="right" style={{ color: "#F26B1F" }}>
                  {i.adjusted_rate.toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function MaterialsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/cost/materials/prices").then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div style={{ fontSize: 12, color: "var(--pf-muted)" }}>Loading…</div>;
  return (
    <div data-testid="cost-materials-tab">
      <div style={{ fontSize: 11, color: "var(--pf-muted)", marginBottom: 8 }}>
        As of {data.as_of} · Rising (≥4%): {data.summary.rising_items.join(", ") || "—"} ·
        {" "}Falling: {data.summary.falling_items.join(", ") || "—"}
      </div>
      {Object.entries(data.categories).map(([cat, items]) => (
        <div key={cat} style={{ marginBottom: 14 }}>
          <div className="pf-overline" style={{ marginBottom: 4 }}>{cat}</div>
          <table style={{ width: "100%", fontSize: 11 }}>
            <thead><tr style={{ color: "var(--pf-muted)" }}>
              <th align="left">Material</th><th align="left">Unit</th>
              <th align="right">Low (INR)</th><th align="right">High (INR)</th>
              <th align="left">3M trend</th><th align="left">12M trend</th>
            </tr></thead>
            <tbody>
              {items.map(it => (
                <tr key={it.item} style={{ borderTop: "1px solid var(--pf-border)" }}>
                  <td>{it.item}</td><td>{it.unit}</td>
                  <td align="right">{it.low.toLocaleString("en-IN")}</td>
                  <td align="right">{it.high.toLocaleString("en-IN")}</td>
                  <td>{trendChip(it.trend_3m)}</td>
                  <td>{trendChip(it.trend_12m)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function trendChip(t) {
  if (!t) return "—";
  const color = t.startsWith("+") ? "#10B981"
                  : t.startsWith("-") ? "#EF4444" : "var(--pf-muted)";
  return <span style={{ color }}>{t}</span>;
}


function BenchmarksTab() {
  const [domain, setDomain]   = useState("real_estate");
  const [subtype, setSubtype] = useState("residential_economy");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const subtypes = {
    real_estate: ["residential_economy", "residential_premium", "commercial_office",
                          "hospital", "hotel_3star"],
    infrastructure: ["national_highway_4lane", "expressway_6lane", "bridge_rcc", "tunnel"],
    process_plant: ["refinery", "chemical", "pharmaceutical", "water_treatment"],
  };
  useEffect(() => {
    setData(null); setErr("");
    api.get(`/cost/benchmarks/${domain}/${subtype}`)
        .then(r => setData(r.data))
        .catch(e => setErr(formatErr(e?.response?.data?.detail) || String(e)));
  }, [domain, subtype]);
  return (
    <div data-testid="cost-benchmarks-tab">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                          gap: 8, marginBottom: 8 }}>
        <Field label="Domain">
          <select data-testid="bench-domain" value={domain}
              onChange={e => { setDomain(e.target.value); setSubtype(subtypes[e.target.value][0]); }}
              style={inp}>
            <option value="real_estate">Real estate</option>
            <option value="infrastructure">Infrastructure</option>
            <option value="process_plant">Process plant</option>
          </select>
        </Field>
        <Field label="Subtype">
          <select data-testid="bench-subtype" value={subtype}
              onChange={e => setSubtype(e.target.value)} style={inp}>
            {subtypes[domain].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      {err && <div style={errBox}>⚠ {err}</div>}
      {data && (
        <div data-testid="bench-result" style={{ padding: 14, background: "var(--pf-card)",
                              border: "1px solid var(--pf-border)", borderRadius: 6 }}>
          <div style={{ fontSize: 22, color: "#F26B1F", fontWeight: 700 }}>
            {data.benchmark.low} – {data.benchmark.avg} – {data.benchmark.high}
          </div>
          <div style={{ fontSize: 12, color: "var(--pf-muted)", marginTop: 4 }}>
            Range (low · avg · high) · Unit: <strong>{data.benchmark.unit}</strong>
          </div>
        </div>
      )}
    </div>
  );
}


function WhatIfTab({ projectId }) {
  const [kind, setKind] = useState("inflate");
  const [pct, setPct] = useState("20");
  const [material, setMaterial] = useState("steel");
  const [from_, setFrom] = useState("BRICK_RED");
  const [to_, setTo] = useState("BRICK_AAC");
  const [factor, setFactor] = useState("0.83");  // G+12 → G+10
  const [base, setBase] = useState(null);
  const [out, setOut] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Reuse last estimate from the project
    api.post(`/cost/estimate`, {
      project_id: `${projectId}_whatif_seed`,
      project_name: "What-if seed",
      domain: "real_estate", design_stage: "FEED", location: "Delhi NCR",
      total_area_m2: 1500,
      quantities: [
        { item_code: "RCC_M25", quantity: 100 },
        { item_code: "REBAR_FE500", quantity: 10 },
        { item_code: "BRICK_RED", quantity: 200 },
      ], save: false,
    }).then(r => setBase(r.data)).catch(() => {});
  }, [projectId]);

  const run = async () => {
    if (!base) return;
    setBusy(true); setErr(""); setOut(null);
    let scenario = { kind };
    if (kind === "inflate") scenario = { kind, material, pct: parseFloat(pct) };
    if (kind === "swap_item") scenario = { kind, from_code: from_, to_code: to_ };
    if (kind === "height_change") scenario = { kind, factor: parseFloat(factor) };
    try {
      const r = await api.post("/cost/what-if",
                                          { base_estimate: base, scenario });
      setOut(r.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
    setBusy(false);
  };

  return (
    <div data-testid="cost-whatif-tab">
      <div style={{ fontSize: 11, color: "var(--pf-muted)", marginBottom: 10 }}>
        Examples: "What if I use VRF instead of central AC?" · "What if I change CS to SS316L piping?" ·
        "What if I reduce building height G+12 → G+10?" · "What if steel inflates 20%?"
      </div>
      <Field label="Scenario kind">
        <select data-testid="whatif-kind" value={kind}
            onChange={e => setKind(e.target.value)} style={inp}>
          <option value="inflate">Material inflation</option>
          <option value="swap_item">Swap item</option>
          <option value="height_change">Scale (height/area change)</option>
        </select>
      </Field>
      {kind === "inflate" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                            gap: 8, marginTop: 8 }}>
          <Field label="Material keyword">
            <input data-testid="whatif-material" value={material}
                onChange={e => setMaterial(e.target.value)} style={inp} />
          </Field>
          <Field label="% change">
            <input data-testid="whatif-pct" value={pct}
                onChange={e => setPct(e.target.value)} style={inp} />
          </Field>
        </div>
      )}
      {kind === "swap_item" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                            gap: 8, marginTop: 8 }}>
          <Field label="From code">
            <select value={from_} onChange={e => setFrom(e.target.value)} style={inp}>
              {ITEM_CODES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="To code">
            <select value={to_} onChange={e => setTo(e.target.value)} style={inp}>
              {ITEM_CODES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
      )}
      {kind === "height_change" && (
        <Field label="Scale factor (e.g. 0.83 for G+12 → G+10)">
          <input data-testid="whatif-factor" value={factor}
              onChange={e => setFactor(e.target.value)} style={inp} />
        </Field>
      )}
      <button data-testid="whatif-run-btn" onClick={run} disabled={busy || !base}
          className="btn btn-primary" style={{ marginTop: 10, fontSize: 12 }}>
        {busy ? <Loader2 size={14} className="pf-spin" /> : <Zap size={14} />}
        Run scenario
      </button>
      {err && <div style={errBox}>⚠ {err}</div>}
      {out && (
        <div data-testid="whatif-result" style={{ marginTop: 12, padding: 14,
                            background: out.delta_inr < 0 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                            borderRadius: 6 }}>
          <div style={{ fontSize: 22, fontWeight: 700,
                              color: out.delta_inr < 0 ? "#10B981" : "#EF4444" }}>
            {out.delta_inr >= 0 ? "+" : ""} INR {out.delta_inr.toLocaleString("en-IN")}
          </div>
          <div style={{ fontSize: 12, color: "var(--pf-muted)" }}>
            Δ {out.delta_pct}% on direct subtotal (new subtotal: INR {out.new_subtotal.toLocaleString("en-IN")})
          </div>
        </div>
      )}
    </div>
  );
}


function OptimisationsTab({ projectId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/cost/optimization-suggestions/${projectId}`)
        .then(r => setData(r.data)).catch(() => setData({ items: [], note: "Run an estimate first." }));
  }, [projectId]);
  if (!data) return <div style={{ fontSize: 12, color: "var(--pf-muted)" }}>Loading…</div>;
  return (
    <div data-testid="cost-opt-tab">
      <div style={{ fontSize: 11, color: "var(--pf-muted)", marginBottom: 10 }}>
        Suggestions inferred from BOQ rules. Run an estimate first to seed.
      </div>
      {data.note && <div style={{ fontSize: 12, color: "var(--pf-muted)" }}>{data.note}</div>}
      {data.items.map((s, i) => (
        <div key={i} style={{ padding: 12, background: "var(--pf-card)",
                                    border: "1px solid var(--pf-border)",
                                    borderRadius: 6, marginBottom: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 600, fontSize: 12 }}>
              <Lightbulb size={14} style={{ display: "inline", marginRight: 4, color: "#F26B1F" }} />
              {s.title}
            </div>
            <div style={{ color: "#10B981", fontSize: 13, fontWeight: 700 }}>
              INR {s.estimated_saving_inr.toLocaleString("en-IN")}
            </div>
          </div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)", marginTop: 4 }}>{s.details}</div>
        </div>
      ))}
    </div>
  );
}


function InflationTab({ projectId }) {
  const [years, setYears] = useState(3);
  const [matFrac, setMatFrac] = useState("0.65");
  const [labInf, setLabInf] = useState("7");
  const [base, setBase] = useState(null);
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.post("/cost/estimate", {
      project_id: `${projectId}_infl_seed`, project_name: "Inflation seed",
      domain: "real_estate", design_stage: "FEED", location: "Delhi NCR",
      total_area_m2: 1500,
      quantities: [
        { item_code: "RCC_M25", quantity: 100 },
        { item_code: "REBAR_FE500", quantity: 10 },
        { item_code: "BRICK_RED", quantity: 200 },
      ], save: false,
    }).then(r => setBase(r.data)).catch(() => {});
  }, [projectId]);

  const run = async () => {
    if (!base) return;
    setBusy(true); setErr(""); setOut(null);
    try {
      const r = await api.post("/cost/inflation-projection", {
        base_estimate: base,
        duration_years: parseInt(years, 10) || 3,
        material_fraction: parseFloat(matFrac) || 0.65,
        labour_inflation: (parseFloat(labInf) || 7) / 100,
      });
      setOut(r.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
    setBusy(false);
  };

  const downloadPdfWithInflation = async () => {
    try {
      const r = await api.get(
        `/cost/report/${projectId}?duration_years=${years}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (_) { /* ok */ }
  };

  return (
    <div data-testid="cost-inflation-tab">
      <div style={{ fontSize: 11, color: "var(--pf-muted)", marginBottom: 10 }}>
        Spec §2.3 — for projects with long construction periods (2–5 years),
        apply annual inflation separately to material vs labour, with a
        year-wise S-curve cash flow.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                          gap: 8, marginBottom: 10 }}>
        <Field label="Construction duration (yrs)">
          <input data-testid="infl-years" value={years}
              onChange={e => setYears(e.target.value)} style={inp} />
        </Field>
        <Field label="Material fraction (0-1)">
          <input data-testid="infl-mat-frac" value={matFrac}
              onChange={e => setMatFrac(e.target.value)} style={inp} />
        </Field>
        <Field label="Labour inflation (% / yr)">
          <input data-testid="infl-lab-pct" value={labInf}
              onChange={e => setLabInf(e.target.value)} style={inp} />
        </Field>
      </div>
      <button data-testid="infl-run-btn" onClick={run}
          disabled={busy || !base} className="btn btn-primary"
          style={{ fontSize: 12 }}>
        {busy ? <Loader2 size={14} className="pf-spin" /> : <TrendingUp size={14} />}
        Project cash flow
      </button>
      <button data-testid="infl-pdf-btn" onClick={downloadPdfWithInflation}
          className="btn btn-ghost"
          style={{ fontSize: 12, marginLeft: 8 }}>
        <Download size={14} /> PDF with cash flow
      </button>
      {err && <div style={errBox}>⚠ {err}</div>}
      {out && (
        <div data-testid="infl-result" style={{ marginTop: 14, padding: 14,
                                background: "var(--pf-card)",
                                border: "1px solid var(--pf-border)",
                                borderRadius: 6 }}>
          <div style={{ fontSize: 12, color: "var(--pf-muted)" }}>
            Material inflation (weighted): <strong style={{ color: "#F26B1F" }}>
              {out.weighted_material_inflation_pct}% / yr</strong>
            {" "}· Labour inflation: <strong style={{ color: "#F26B1F" }}>
              {out.labour_inflation_pct}% / yr</strong>
          </div>
          <table style={{ width: "100%", marginTop: 10, fontSize: 11,
                                fontFamily: "'JetBrains Mono', monospace" }}>
            <thead><tr style={{ color: "var(--pf-muted)" }}>
              <th align="left">Year</th>
              <th align="right">Spend %</th>
              <th align="right">Mat factor</th>
              <th align="right">Lab factor</th>
              <th align="right">Nominal INR</th>
            </tr></thead>
            <tbody>
              {out.cash_flow.map(r => (
                <tr key={r.year} style={{ borderTop: "1px solid var(--pf-border)" }}>
                  <td>Year {r.year}</td>
                  <td align="right">{(r.fraction * 100).toFixed(1)}%</td>
                  <td align="right">{r.material_factor.toFixed(3)}</td>
                  <td align="right">{r.labour_factor.toFixed(3)}</td>
                  <td align="right" style={{ color: "#F26B1F" }}>
                    {r.nominal_inr.toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12, padding: 10,
                              background: "rgba(242,107,31,0.1)",
                              borderRadius: 6, fontSize: 13 }}>
            <strong>Inflation-adjusted total:</strong>
            {" "}INR {out.inflation_adjusted_total.toLocaleString("en-IN")}
            {" "}<span style={{ fontSize: 11, color: "var(--pf-muted)" }}>
              (Δ {out.delta_vs_base_subtotal_pct}% on direct subtotal)
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── shared ─────────────────────────────────────────────────────────
const inp = { fontSize: 12, padding: 6, width: "100%" };
const errBox = { color: "#EF4444", fontSize: 11, marginTop: 8 };

function Field({ label, children }) {
  return (
    <div>
      <div className="pf-overline" style={{ marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div style={{ display: "contents" }}>{children}</div>;
}
function KV({ k, v, highlight }) {
  return (
    <div style={{ background: highlight ? "rgba(242,107,31,0.15)" : "transparent",
                        padding: highlight ? 8 : 0, borderRadius: 4 }}>
      <div style={{ fontSize: 10, color: "var(--pf-muted)",
                          textTransform: "uppercase" }}>{k}</div>
      <div style={{ fontSize: 13, fontWeight: 600,
                          color: highlight ? "#F26B1F" : "var(--pf-text)",
                          fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
    </div>
  );
}
